/**
 * Central catalog of analytics events.
 *
 * Every custom (non-pageview) event the site tracks is declared here as a named method, so this
 * file is the single source of truth for "what are we measuring." Pageviews are automatic; these
 * are the in-page interactions (AJAX, clicks) that change no URL and would otherwise be invisible.
 *
 * Events fire to Umami (`umami.track(name, data)`), which lists them automatically with no
 * dashboard setup. In dev/localhost the script is not present, so every call is a silent no-op.
 * Errors are swallowed: analytics must never break the page.
 *
 * To add an event: add a method here (don't call the tool globals directly from components), then
 * invoke `analytics.<name>(…)` from the component.
 */

interface UmamiGlobal {
  track?: (name: string, data?: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    umami?: UmamiGlobal;
  }
}

/** Low-level dispatch. Kept private so the catalog below stays the only API. */
function track(name: string, props: Record<string, unknown> = {}): void {
  try {
    window.umami?.track?.(name, props);
  } catch {
    /* analytics must never interfere with the page */
  }
}

export const analytics = {
  /** Home job feed (FeaturedJobs): visitor clicked "load more" and a page loaded successfully. */
  loadMore(page: number, jobsShown: number): void {
    track('Load More', { page, jobsShown });
  },

  /**
   * Home job feed: visitor clicked a job title through to its detail page. `position` is 1-based
   * across everything loaded so far and reads against `total`, which answers whether the pages
   * behind "load more" earn any clicks or only the first screen does. The active filters are NOT
   * repeated here — they are query parameters on the home page, and Umami stamps the page's query
   * string onto the event row already.
   */
  feedJobClick(position: number, total: number, company: string): void {
    track('Feed Job Click', { position, total, company });
  },

  /**
   * Find-jobs: Enter or the button found no role for what was typed, so the page held the visitor
   * instead of submitting. This is the taxonomy-gap signal — the phrase people use for a job we
   * have no role for — and it fires from here because the gate means the server never sees the
   * miss. `keyword` is already trimmed, lower-cased and capped by the island.
   */
  findJobsNoMatch(keyword: string): void {
    track('Find Jobs No Match', { keyword });
  },

  /**
   * Alert-signup modal opened. `mode` separates the soft "get these by email" prompt from the hard
   * verify-wall — a `wall` value also marks that the visitor hit the anonymous depth cap.
   */
  signupPromptShown(mode: 'soft' | 'wall'): void {
    track('Signup Prompt Shown', { mode });
  },

  /** Alert signup accepted server-side (opt-in email sent). The real conversion. */
  signupCompleted(mode: 'soft' | 'wall'): void {
    track('Signup Completed', { mode });
  },

  /**
   * Alert signup rejected at submit. `reason` is `domain_blocked` (disposable inbox / no mail
   * server — our server-side rejection) or `invalid_format` (bean-validation bounce). Lets us watch
   * how often the disposable-email block fires in production.
   */
  signupRejected(reason: 'domain_blocked' | 'invalid_format'): void {
    track('Signup Rejected', { reason });
  },
};
